<template>
    <q-tab-panel name="EventManagementTab">
        <div class="q-mb-md">
            {{ ts( 'saveToFileHint' ) }}
        </div>

        <q-form autofocus>
            <q-btn
                :label="ts( 'saveToFile' )" outline color="secondary"
                @click="saveToFile()" />
        </q-form>

        <div class="q-mt-xl q-mb-md">
            {{ ts( 'sendToCsjHint' ) }}
        </div>

        <q-form>
            <q-btn
                :label="ts( 'sendToCsj' )" outline color="primary"
                @click="sendToCsj()" />
        </q-form>

        <div class="q-mt-xl q-mb-md">
            {{ ts( 'removeEventHint' ) }}
        </div>

        <q-form>
            <q-btn
                :label="ts( 'removeEvent' )" outline color="negative"
                @click="removeEvent()" />
        </q-form>
    </q-tab-panel>
</template>

<script setup lang="ts">
import Confirmation from "@/components/dialogs/Confirmation.vue"
import { authenticate, checkAuth, showError } from "@/network/auth"
import { encodeBoatClass } from "@/network/decoders"
import { exportEvent } from "@/network/exporter"
import apiRequest from "@/network/httpClient"
import EventsRepo from "@/orm/repositories/EventsRepo"
import { DateFormat, ds, ts } from '@/plugins/i18n-formatted'
import InfoBox from "@dialogs/InfoBox.vue"
import IssueList from "@dialogs/IssueList.vue"
import LogIn from "@dialogs/LogIn.vue"
import EventResult from "@model/EventResult"
import { Defaults, Options } from "@model/Setting"
import SettingsRepo from "@repo/SettingsRepo"
import { useRepo } from "pinia-orm"
import { useQuasar } from "quasar"
import { sprintf } from 'sprintf-js'
import { ref } from "vue"
import { useRoute, useRouter } from "vue-router"
import { saveAs } from "file-saver"
import deterministicStringify from "json-stringify-deterministic"

const quasar = useQuasar()
const router = useRouter()
const route = useRoute()

const event = ref( useRepo( EventsRepo ).getEvent( String( route.params.eventId ) ) )

useRepo( EventsRepo ).selectEvent( event.value.id )

function removeEvent () {
    quasar.dialog( {
        component: Confirmation,
        componentProps: {
            icon: 'delete',
            question: ts( 'reallyRemoveEvent', { eventTitle: event.value.fullTitle } )
        }
    } ).onOk( () => {
        useRepo( EventsRepo ).destroy( event.value.id )

        router.push( { name: 'MainPage' } )
    } )
}

function saveToFile () {
    const data = JSON.stringify( exportEvent( event.value ) )

    const blob = new Blob( [ data ], { type: "application/json; charset=utf-8" } )

    event.value?.setModified( false )

    saveAs( blob, 'event-' + event.value.ctlId + '.json' )
}

function sendToCsj () {
    const issueList = []

    const checkedEvent = useRepo( EventsRepo ).getEvent( event.value.id, true )

    if ( checkedEvent.title.trim() === '' )
        issueList.push( { severity: 'crit', what: ts( 'noEventTitle' ) } )

    if ( String( checkedEvent.ctlId ).length !== 6 || String( checkedEvent.ctlId ).substring( 0, 2 ) !== String( new Date().getFullYear() ).substring( 2, 4 ) )
        issueList.push( { severity: 'crit', what: ts( 'badCtlIdFormat', { ctlId: checkedEvent.ctlId } ) } )

    if ( checkedEvent.to !== null && checkedEvent.from > checkedEvent.to )
        issueList.push( { severity: 'crit', what: ts( 'invalidEventTimespan' ) } )

    if ( checkedEvent.clubId === null && ( checkedEvent.organizer ?? '' ).trim() === '' )
        issueList.push( { severity: 'crit', what: ts( 'invalidOrganizer' ) } )

    if ( checkedEvent.venueId === null && ( checkedEvent.otherVenue ?? '' ).trim() === '' )
        issueList.push( { severity: 'crit', what: ts( 'invalidVenue' ) } )

    if ( ( checkedEvent.director ?? '' ).trim() === '' )
        issueList.push( { severity: 'crit', what: ts( 'invalidDirector' ) } )

    if ( checkedEvent.classes.length === 0 )
        issueList.push( { severity: 'crit', what: ts( 'noSelectedClasses' ) } )

    if ( checkedEvent.referees.length === 0 )
        issueList.push( { severity: 'crit', what: ts( 'noSelectedReferee' ) } )
    else {
        if ( !checkedEvent.referees.some( ( referee ) => referee.refereeRole.isMainReferee ) && !checkedEvent.useCourses )
            issueList.push( { severity: 'crit', what: ts( 'noMainReferee' ) } )
        else if ( checkedEvent.referees.filter( ( referee ) => referee.refereeRole.isMainReferee ).length > 1 )
            issueList.push( { severity: 'crit', what: ts( 'moreThanOneMainReferee' ) } )

        if ( checkedEvent.useCourses && checkedEvent.referees.filter( ( referee ) => referee.refereeRole.isMainCourseReferee ).length !== checkedEvent.courses.length )
            issueList.push( { severity: 'crit', what: ts( 'incorrectNumberOfCoursesReferees' ) } )
    }

    if ( checkedEvent.referees.filter( ( referee ) => referee.refereeRole.isChiefUmpire ).length > 1 )
        issueList.push( { severity: 'crit', what: ts( 'moreThanOneChiefUmpire' ) } )

    if ( checkedEvent.referees.filter( ( referee ) => referee.refereeRole.isJuryChairman ).length > 1 )
        issueList.push( { severity: 'crit', what: ts( 'moreThanOneJuryChairman' ) } )

    checkedEvent.referees.forEach( function ( referee ) {
        if ( checkedEvent.referees.filter( ( r ) => r.refereeId === referee.refereeId ).length > 1 )
            issueList.push( { severity: 'crit', what: ts( 'refereeWithMoreRoles', { referee: referee.referee.fullNameWithIdentification } ) } )
    } )

    if ( checkedEvent.lifeguards.length === 0 && !checkedEvent.noLifeguards )
        issueList.push( { severity: 'crit', what: ts( 'noSelectedLifeguards' ) } )
    else if ( !checkedEvent.noLifeguards && !checkedEvent.lifeguards.some( ( lifeguard ) => lifeguard.lifeguardRole.isMainLifeguard ) )
        issueList.push( { severity: 'warn', what: ts( 'noMainLifeguard' ) } )
    else if ( !checkedEvent.noLifeguards && checkedEvent.lifeguards.filter( ( lifeguard ) => lifeguard.lifeguardRole.isMainLifeguard ).length > 1 )
        issueList.push( { severity: 'crit', what: ts( 'moreThanOneMainLifeguard' ) } )

    checkedEvent.lifeguards.forEach( function ( lifeguard ) {
        if ( checkedEvent.lifeguards.filter( ( l ) => l.lifeguardId === lifeguard.lifeguardId ).length > 1 )
            issueList.push( { severity: 'crit', what: ts( 'lifeguardWithMoreRoles', { lifeguard: lifeguard.lifeguard.fullNameWithIdentification } ) } )
    } )

    if ( checkedEvent.boats.length === 0 )
        issueList.push( { severity: 'crit', what: ts( 'noRegisteredBoats' ) } )

    if ( checkedEvent.races.length === 0 )
        issueList.push( { severity: 'warn', what: ts( 'noRacesOnEvent' ) } )

    if ( checkedEvent.boats.some( ( boat ) => boat.crew.length === 0 ) )
        issueList.push( { severity: 'crit', what: ts( 'boatWithoutCrew' ) } )

    if ( checkedEvent.boats.some( ( boat ) => boat.isLicenseIssue ) )
        issueList.push( { severity: 'warn', what: ts( 'licenseIssueOnEvent' ) } )

    if ( checkedEvent.boats.some( ( boat ) => boat.crew.filter( ( c ) => c.isPublic ).length === boat.crew.length && boat.crew.length > 0 ) )
        issueList.push( { severity: 'crit', what: ts( 'onlyPublicBoat' ) } )

    checkedEvent.classes.forEach( function ( eventClass ) {
        if ( checkedEvent.boats.filter( ( boat ) => boat.boatClassId === eventClass.boatClassId ).length === 0 )
            issueList.push( { severity: 'warn', what: ts( 'classWithoutBoats', { boatClass: eventClass.boatClass.name } ) } )
        else if ( useRepo( EventResult ).whereHas( 'boat', ( q ) => q.where( 'eventId', checkedEvent.id ).where( 'boatClassId', eventClass.boatClassId ) ).get().length === 0 )
            issueList.push( { severity: 'warn', what: ts( 'noResultsInClass', { boatClass: eventClass.boatClass.name } ) } )
        else if ( checkedEvent.results( eventClass.boatClassId ).some( ( boat ) => boat.results.some( r => !r.decided ) ) )
            issueList.push( { severity: 'warn', what: ts( 'decidedResultsInClass', { boatClass: eventClass.boatClass.name } ) } )

        if ( eventClass.publicMode === 1 && checkedEvent.boats.some( ( boat ) => boat.captain?.isPublic ) )
            issueList.push( { severity: 'crit', what: ts( 'publicType1Violated' ) } )
    } )

    if ( issueList.length === 0 )
        issueList.push( { severity: 'ok', what: ts( 'everythingOk' ) } )
    else if ( !issueList.some( ( i ) => i.severity === 'crit' ) )
        issueList.push( { severity: 'ok', what: ts( 'noCritIssues' ) } )

    quasar.dialog( {
            component: IssueList,
            componentProps: {
                issueList: issueList
            }
        } )
        .onOk( async function () {
            if ( await checkAuth() !== 'auth' ) {
                await logIn()

                return
            }

            await performUpload()
        } )
}

function logIn ( attempts = 3, error = 0 ) {
    quasar.dialog( {
        component: LogIn,
        componentProps: {
            errorMsg: showError( error )
        }
    } ).onOk( async ( payload ) => {
        const lastError = await authenticate( payload.username, payload.password )

        if ( !isAuthenticated.value ) {
            if ( attempts > 1 )
                logIn( attempts - 1, lastError )
            else
                quasar.dialog( {
                    component: InfoBox,
                    componentProps: {
                        message: ts( 'tooManyAttempts' )
                    }
                } )
        } else {
            await performUpload()
        }
    } )
}

async function computeDigest ( data ) {
    const payload = {
        message: data,
        sharedSecret: 'Mwvry6K9D4mgqP9lEqAxQuLiS3AWGhPbkIbCienvtYdnYKe6mpwmMbKMt8zypNTgdJbGlGRg5ZM6fUxeVvGw0sCUeGyINFBNslogyP2aUJdB33fc1ILd3FrJbLDcyzOr1nezjjqTHxeZFYRKiz27hiLelx01RsaU0A1M9WU0ulhCIHrZ7CMKDKozjOSQvoGSri3wntThOzZ96tYDrJPOdxYsNztOeAshk3MJB7VBCeMV1rxF1TetJZGTxuvFvlRSIrufLveTTERLGbNapgQO8Ef506xnAfHwXUyZIfV3mXaK'
    }

    const msgUint8 = new TextEncoder().encode( deterministicStringify( payload ) );

    const hashBuffer = await window.crypto.subtle.digest( 'SHA-256', msgUint8 );

    const hashArray = Array.from( new Uint8Array( hashBuffer ) );

    return hashArray.map( ( b ) => b.toString( 16 ).padStart( 2, '0' ) ).join( '' );
}

async function performUpload () {
    const checkedEvent = useRepo( EventsRepo ).getEvent( event.value.id, true )

    if ( checkedEvent === null )
        return

    const data = JSON.stringify( exportEvent( checkedEvent ) )

    let payload = {
        event: {
            id: checkedEvent.ctlId,
            name: checkedEvent.title.trim(),
            clubId: checkedEvent.clubId,
            organizer: checkedEvent.organizer === null ? null : checkedEvent.organizer.trim(),
            venueId: checkedEvent.venueId,
            otherVenue: checkedEvent.otherVenue === null ? null : checkedEvent.otherVenue.trim(),
            sponsor: checkedEvent.sponsor?.trim(),
            from: ds( checkedEvent.from, DateFormat.SystemDate ),
            to: checkedEvent.to === null ? null : ds( checkedEvent.to, DateFormat.SystemDate ),
            days: checkedEvent.to === null ? 1 : null,
            classes: checkedEvent.classes.map( ( eventClass ) => ( {
                classId: encodeBoatClass( eventClass.boatClassId ),
                weight: eventClass.weight,
                eventType: eventClass.eventType,
                shortcut: eventClass.shortcut,
                discardDefinition: eventClass.discardDefinition,
                className: eventClass.className
            } ) ),
            courses: checkedEvent.courses.map( ( course ) => ( {
                title: course.title
            } ) ),
            regattaDirector: {
                name: checkedEvent.director.trim()
            },
            referees: checkedEvent.referees.map( ( referee ) => ( {
                refereeId: referee.referee.id,
                roleId: referee.refereeRole.id,
                memberId: referee.referee.regId
            } ) ),
            lifeguards: checkedEvent.lifeguards.map( ( lifeguard ) => ( {
                lifeguardId: lifeguard.lifeguard.id,
                roleId: lifeguard.lifeguardRole.id,
                memberId: lifeguard.lifeguard.regId
            } ) )
        },
        results: checkedEvent.classes.map( ( eventClass ) => ( {
            classId: encodeBoatClass( eventClass.boatClassId ),
            plannedCoefficient: eventClass.weight,
            calculatedCoefficient: eventClass.calculatedWeight,
            scoreboard: checkedEvent.results( eventClass.boatClassId ).map( ( boat ) => ( {
                country: boat.boat.country,
                sailNo: boat.boat.sailNumber,
                name: boat.boat.name,
                rank: boat.index,
                points: boat.points,
                logPoints: boat.logPoints,
                races: boat.results.map( ( result ) => ( {
                    rank: result.details?.order,
                    points: result.points,
                    other: result.decided ? result.details?.other : 'DNC',
                    attr: result.details?.attr,
                    discarded: result.isDiscarded,
                    guessed: !result.decided
                } ) ),
                crew: boat.boat.crew.map( ( crew ) => ( {
                    memberId: Number( crew.regId.replace( '-', '' ) ),
                    firstName: crew.firstName.trim(),
                    lastName: crew.lastName.trim(),
                    birthYear: crew.birthYear,
                    sex: crew.sex,
                    isCaptain: crew.captain,
                    phone: ( crew.phone === undefined || crew.phone === null ) ? null : crew.phone.trim(),
                    email: ( crew.email === undefined || crew.email === null ) ? null : crew.email.trim(),
                    citizenship: crew.nonMemberCitizenship,
                    birthDate: ds( crew.birthDate, DateFormat.SystemDate ),
                    birthCode: crew.isOneTime ? crew.nonMemberBirthCode?.replace( '/', '' ) : null,
                    address: ( crew.nonMemberAddress === undefined || crew.nonMemberAddress === null ) ? null : crew.nonMemberAddress.trim()
                } ) )
            } ) )
        } ) ),
        storeToCloud: {
            ctlId: checkedEvent.ctlId,
            eventTitle: checkedEvent.title,
            workFile: data
        },
        digests: {
            event: null,
            results: null
        }
    }

    payload.digests.event = await computeDigest( payload.event )
    payload.digests.results = await computeDigest( payload.results )

    apiRequest( useRepo( SettingsRepo ).getOption( Options.AuthToken, Defaults.AuthToken ) ).post( '/events/' + checkedEvent.ctlId + '/results', payload )
        .then( ( response ) => {
                if ( response.status === 202 ) {
                    event.value?.setModified( false )

                    quasar.notify( {
                        icon: 'sailing',
                        color: 'positive',
                        message: ts( 'resultsUploaded' ),
                        timeout: 5000
                    } )
                }
            }
        )
        .catch( () => {
            quasar.notify( {
                icon: 'error',
                color: 'negative',
                message: ts( 'cannotUploadResults' ),
                timeout: 10000
            } )
        } )
}
</script>
